/**
 * BREADCRUMB_NAMESPACES — the SINGLE source of truth for every i18n namespace a
 * route may reference via `staticData.breadcrumb.labelKey`.
 *
 * Consumed by every route-label reader so they can never drift:
 *   - <RouterBreadcrumbs> (in-content chevron trail)
 *   - useRouteTitle       (the top bar's fallback page title)
 *
 * Both call `useTranslation(BREADCRUMB_NAMESPACES)` so i18next eagerly preloads
 * every listed lazy bundle before the first `t("ns:key")` fires. If a route's
 * breadcrumb `labelKey` names a namespace that is NOT in this list, the `t()`
 * call runs before that lazy bundle has landed — i18next emits a spurious
 * "[i18n] Missing key" warning and the raw key renders as the label for one
 * paint cycle. `scripts/check-i18n.mjs` won't catch this (it's runtime lazy-
 * load behavior, not a static-key absence), so this list is the source of truth.
 *
 * Add a namespace here the moment a new route's breadcrumb starts using it.
 * Before this shared module, the old topbar trail kept its own (shorter) copy and
 * silently lacked `payroll` / `reports` etc. once the payroll + reports
 * routes started declaring `payroll:nav.*` / `reports:*` labelKeys — leaving
 * those topbar crumbs unresolved. Keep this the union of every audience's
 * route labelKey namespaces.
 */
export const BREADCRUMB_NAMESPACES = [
  "common",
  "appraisal",
  "approvals", // Phase 15 — /approvals canonical route (IA-01)
  "approval-chains", // Phase 16 — /approvals/chains/* + /settings/approvals-cfg (APPR-01)
  "assets", // /assets + /assets/$id — breadcrumb labelKey assets:admin.assets.page.title
  "attendance",
  "auth",
  "branding",
  "calendar", // Phase 05.2 — /me/calendar (Plan 10)
  "directory", // Phase 05.2 — /me/directory (Plan 11)
  "documents",
  "employees",
  "expenses",
  "holidays",
  "leaves",
  "link-admin",
  "loans", // Phase 05.2 — /me/loans (Plan 12)
  "me-overtime", // /me/overtime route — typed overtime screen (stats + table)
  "me-shifts", // /me/shifts route — caller's resolved + upcoming shift assignments
  "me-profile",
  "org",
  "payroll", // Phase 05.2 — /me/payroll + /payroll/* suite crumbs
  "requests",
  "roles",
  "roles-permissions", // Plan 08.1-05
  "team", // Plan 08.1-08
  "topbar", // Plan 08.1-04 (persona switcher)
  "settings",
  "shifts",
  "disciplinary", // Phase 23 — /disciplinary HR action log + per-employee scope
  "lifecycle", // Phase 20 — /lifecycle hub + /lifecycle/runs/$runId workspace
  "cost-center", // Phase 26 — /payroll/cost-center catalog (CC-FK-01)
  "ot-types", // Phase 26 — /settings/ot-types OT-type catalog (PAY2-04)
  "reports", // Phase 27 — /reports/* builder/saved/scheduled crumbs (CC-143)
  "messages", // CC-76 — /messages chat surface (be-070)
  "assistant", // /assistant conversation page crumb
  "gosi", // /payroll/gosi-readiness — GOSI enrollment readiness crumb
  "hr-letters", // HR letter templates builder (/letter-templates/*)
  "mass-changes", // /employees/mass-changes register + job
  "setup", // /setup first-run wizard — the bar title under the overlay
  "hiring", // /hiring/* — jobs and candidates
] as const;
